/**
 * Hebrew strings — first non-English locale, validates RTL layout.
 * Shape mirrors en.ts exactly. Missing keys fall back to English.
 */

import type { StringCatalog } from './en'

export const strings: StringCatalog = {
  paywall: {
    cta: {
      startTrial:       'התחל תקופת ניסיון של 30 ימים',
      startProPlus:     'שדרוג ל-Pro+',
      startProYearly:   'התחל Pro וחסכו $96 בשנה',
      startPro:         'התחל Pro',
      continueEmail:    'המשך עם אימייל ←',
      loading:          'טוען…',
      sending:          'שולח…',
      checkInbox:       '✓ בדקו את תיבת הדואר שלכם',
      trialHint:        '30 הימים הראשונים חינם. ניתן לבטל בכל עת.',
      noTrialHint:      'ל-Pro+ אין תקופת ניסיון: החיוב היום. ניתן לבטל בכל עת.',
      magicLinkHint:    'נשלח לכם קישור התחברות במייל. אין צורך בסיסמה.',
      promoPrompt:      'יש לכם קוד קופון?',
      promoPlaceholder: 'קוד קופון',
      emailPlaceholder: 'האימייל שלכם',
    },
    billing: {
      monthly:        'חודשי',
      yearly:         'שנתי',
      saveBadgeShort: '7 חודשים חינם',
      perMonth:       '/חודש',
      pricedMonthly:  (p: string) => `${p}/חודש`,
      saveLine:       'חסכו $96 בשנה',
      billedYearly:   (yearly: string) => `חיוב ${yearly}/שנה · 3 מיליון תווים בשנה`,
      billedMonthly:  'חיוב חודשי · ניתן לבטל בכל עת · 250 אלף תווים בחודש',
    },
    footer: {
      stripeSecured:   'מאובטח ע״י Stripe',
      cancelAnytime:   'ניתן לבטל בכל עת',
      noHiddenFees:    'ללא עלויות נסתרות',
      oneSubscription: 'מנוי אחד כולל את האתר והתוסף ל-Chrome.',
    },
    crossPlatform: {
      note: 'Pro עובד גם באייפון ובאנדרואיד. התחברו עם אותו אימייל.',
    },
    features: {
      chars:         '250,000 תווים בחודש (3 מיליון בשנה במסלולים השנתיים)',
      slang:         'מצב סלנג ומצב סיכום ישראליים',
      media:         '40 תמונות ו-10 קבצי PDF בחודש',
      voice:         'האזנה לכל תרגום בקולות איכותיים',
      crossPlatform: 'מנוי אחד לאתר, לתוסף Chrome, ל-iPhone ול-Android',
    },
    reasons: {
      upgrade: {
        badge:    'baba Pro',
        title:    'שדרגו ל-baba Pro',
        subtitle: 'פתחו מגבלות גבוהות יותר, סלנג והשמעה קולית.',
      },
      'monthly-chars-reached': {
        badge:    'נגמרו לכם התווים',
        title:    'קבלו מגבלות גבוהות יותר',
        subtitle: 'תרגמו יותר טקסט, תמונות, קבצי PDF והשמעה קולית.',
      },
      'text-too-long': {
        badge:    'הקטע הזה ארוך מדי',
        title:    'תרגמו קטעים ארוכים יותר',
        subtitle: 'עברו מ-500 ל-20,000 תווים לתרגום.',
      },
      'slang-mode': {
        badge:    'תכונת Pro: מצב סלנג',
        title:    'פתחו את כל ערכי הסלנג',
        subtitle: 'מעבר לרשימה. ראו הגדרות מלאות, דוגמאות והקשר.',
      },
      'summary-mode': {
        badge:    'תכונת Pro: מצב סיכום',
        title:    'סכמו טקסטים ארוכים',
        subtitle: 'קבלו את עיקרי הדברים בלי לקרוא הכל.',
      },
      pdf: {
        badge:    'תכונת Pro: תרגום PDF',
        title:    'תרגמו מסמכי PDF',
        subtitle: 'העלו קובץ PDF (עד 20 עמודים, 10MB) וקבלו תרגום נקי. עד 10 קבצים בחודש עם Pro.',
      },
      image: {
        badge:    'תכונת Pro: תרגום תמונה',
        title:    'תרגמו כל תמונה',
        subtitle: 'צלמו שלט, תפריט או מסך. עד 40 תמונות בחודש עם Pro.',
      },
      document: {
        badge:    'תכונת Pro: תרגום מסמכים',
        title:    'תרגמו PDF ומסמכים',
        subtitle: 'העלו קובץ PDF, Word או PowerPoint (עד 10MB) וקבלו גרסה מתורגמת נקייה, עם שמירה על הפורמט ככל האפשר. עד 15 מסמכים בחודש עם Pro.',
      },
      voice: {
        badge:    'תכונת Pro: השמעה קולית',
        title:    'שמעו עברית בקול רם',
        subtitle: 'האזינו להגייה טבעית לכל תרגום. הקישו פעמיים על מילה כדי לשמוע רק אותה.',
      },
      'word-alternatives': {
        badge:    'תכונת Pro: חלופות מילה',
        title:    'ראו כל דרך לומר את זה',
        subtitle: 'הקישו על מילה כדי לראות ניסוחים חלופיים בהקשר, מושלם ללימוד ניואנסים.',
      },
      favorites: {
        badge:    'תכונת Pro: תרגומים שמורים',
        title:    'שמרו כל משפט ששווה לזכור',
        subtitle: 'במסלול חינמי מוגבלים ל-50 שמורים. Pro שומר הכל. בנו ספר ביטויים עברי משלכם.',
      },
      history: {
        badge:    'תכונת Pro: היסטוריית תרגומים',
        title:    'מצאו כל תרגום, לתמיד',
        subtitle: 'המסלול החינמי שומר 200 תרגומים אחרונים. Pro שומר הכל, עם חיפוש בכל המכשירים.',
      },
      'text-tutor-limit': {
        badge:    'תכונת Pro: המורה אבי',
        title:    'להמשיך ללמוד עם אבי',
        subtitle: 'ניצלתם את 15 התשובות החינמיות של אבי. עם Pro השיחה ממשיכה.',
      },
      'reply-drafts': {
        badge:    'תכונת Pro: טיוטות תשובה',
        title:    'לענות בעברית בלי לנחש',
        subtitle: 'ניצלתם את שלוש טיוטות התשובה החינמיות החודש.',
      },
      'text-tutor-pro-limit': {
        badge:    'תכונת Pro+: אבי המורה',
        title:    'קבלו עוד זמן עם אבי',
        subtitle: 'ניצלתם את 300 התגובות שלכם ב-Pro. Pro+ נותן לכם 1,000 תגובות של אבי כל 30 יום.',
      },
      conversation: {
        badge:    'תכונת Pro: מצב שיחה',
        title:    'דברו, אל תקלידו',
        subtitle: 'שיחות קוליות בזמן אמת, דו-כיווניות או חד-כיווניות. Pro מקבל 15 דקות כל 30 יום, Pro+ מקבל 60.',
      },
      situations: {
        badge:    'תכונת Pro+: סיטואציות יומיות',
        title:    'המשיכו את הסיטואציות היומיות שלכם',
        subtitle: 'ניצלתם את שלוש הסיטואציות החינמיות. Pro+ שולח סיטואציה חדשה כל יום.',
      },
      'voice-tutor': {
        badge:    'תכונת Pro+: מורה קולי',
        title:    'הכירו את אבי',
        subtitle: 'המורה הקולי החי שלכם בעברית, מבוסס AI. שדרגו ל-Pro+ כדי להתחיל לדבר.',
      },
    },
    reasonFeatures: {
      upgrade: [
        'תרגמו עד 20,000 תווים בבת אחת',
        'מצבי סלנג, סיכום ופירוט נפתחים',
        'מגבלות גבוהות יותר לטקסט, תמונות, PDF והשמעה קולית',
      ],
      'monthly-chars-reached': [
        '250,000 תווים בחודש (3 מיליון בשנה במסלולים השנתיים)',
        '50 תרגומי תמונה בחודש',
        '15 תרגומי PDF בחודש',
        '5,000 תווי השמעה קולית בחודש',
      ],
      'text-too-long': [
        'תרגמו עד 20,000 תווים בבת אחת',
        'שלחו שיחות ומסמכים שלמים בלי לפצל',
        'הימנעו מקיצוץ וחלקים חסרים בטקסט',
      ],
      'slang-mode': [
        'הגדרות מלאות לכל מונח סלנג בעברית',
        'משפטי דוגמה מהחיים האמיתיים כדי לראות הקשר',
        'שמרו מועדפים ובקשו מונחים ששמעתם',
      ],
      'summary-mode': [
        'תרגמו וסכמו בלחיצה אחת',
        'ראו נקודות עיקריות במקום קיר טקסט',
        'מושלם למאמרים, שיחות ומסמכים',
      ],
      pdf: [
        '15 תרגומי PDF בחודש עם Pro',
        'תרגום עברית לאנגלית של מסמך שלם',
        'שמירה על פריסה ומבנה קריאים ככל האפשר',
      ],
      image: [
        '50 תרגומי תמונה בחודש עם Pro',
        'תרגמו צילומי מסך, תפריטים ושלטים',
        'שמרו על פריסה והקשר בתוצאות',
      ],
      document: [
        '15 תרגומי מסמכים בחודש עם Pro',
        'קבצי PDF, Word ו-PowerPoint עד 10MB',
        'שמירה על הפורמט ככל האפשר',
      ],
      voice: [
        '5,000 תווי השמעה קולית בחודש עם Pro',
        'קולות עבריים טבעיים מבוססי AI',
        'הקישו פעמיים על מילה כדי לשמוע רק אותה',
      ],
      'word-alternatives': [
        'ניסוחים חלופיים לכל תרגום, בהקשר',
        'ראו ניואנסים שהיו חומקים מתרגום יחיד',
        'מעולה כדי להישמע טבעיים, לא רק נכונים',
      ],
      favorites: [
        'שמירת ביטויים ללא הגבלה, במסלול החינמי עד 50',
        'בנו ספר ביטויים עברי משלכם',
        'מסונכרן בין האתר, התוסף והנייד',
      ],
      history: [
        'כל תרגום נשמר, במסלול החינמי עד 200',
        'חפשו בכל היסטוריית התרגומים שלכם',
        'מסונכרן בין האתר, התוסף והנייד',
      ],
      'text-tutor-limit': [
        '300 תשובות של אבי בכל 30 יום',
        'אבי זוכר את השיחה ואת ההקשר הלימודי שלכם',
        'הוסיפו תמונות, PDF ומסמכים לשאלות',
        'כל מה שיש ב-baba Pro כלול',
      ],
      'reply-drafts': [
        'שתי תשובות בעברית מוכנות לשליחה לכל הודעה שקיבלתם',
        'כל תשובה מוצגת גם בשפה שלכם, כדי שתדעו מה אתם שולחים',
        'אמרו מה אתם מתכוונים וקבלו את זה כתוב נכון בעברית',
        'העתיקו או שלחו ישירות ממסך התוצאה',
      ],
      'text-tutor-pro-limit': [
        '1,000 תגובות שלמות מאבי כל 30 יום',
        '60 דקות של מורה קולי חי בכל חודש',
        '60 דקות של מצב שיחה חי בכל חודש',
        'כל מה שיש ב-baba Pro כלול',
      ],
      conversation: [
        'דו-כיווני: שני אנשים מדברים ו-baba מתרגם בזמן אמת בשני הכיוונים',
        'חד-כיווני: אתם מדברים ו-baba אומר את התרגום בקול',
        'מושלם לטיולים, פגישות עם מקומיים, או סגירת עסקה בחו״ל',
      ],
      situations: [
        'סיטואציה אחת אמיתית בעברית כל יום בוואטסאפ',
        'baba משחק את הצד השני ומתקן אתכם תוך כדי',
        'הגייה עם כל תיקון',
        'תשעים סיטואציות, מהזמנת פלאפל ועד ויכוח על חשבון',
      ],
      'voice-tutor': [
        'Pro+ כולל 60 דקות מורה ו-60 דקות שיחה בכל חודש',
        'שיחות קוליות אמיתיות עם מורה ישראלי שנשמע טבעי',
        'מדבר בצורה הנכונה בשבילכם, זכר או נקבה, קובעים פעם אחת ושוכחים מזה',
        'תרגלו הזמנות, שאלות כיוונים, דקדוק וסלנג, מה שתצטרכו',
        'כל מה שיש ב-baba Pro כלול',
      ],
    },
    tierToggle: {
      pro:     'Pro',
      proPlus: 'Pro+',
      badge:   'AI',
    },
    proPlusBenefits: {
      label:      'מה מקבלים',
      proBadge:   'PRO',
      plusBadge:  'PRO+',
      proHeader:  'כל מה שיש ב-baba Pro',
      plusHeader: 'ובנוסף: אבי המורה שלכם, וסיטואציות יומיות',
      proItems: [
        '20,000 תווים לתרגום',
        'מצבי סלנג, סיכום ופירוט',
        'קלט קולי בתוספת 15 דקות שיחה בחודש',
        'תרגום תמונות ו-PDF',
      ],
      plusItems: [
        '60 דקות של מורה קולי בחודש',
        '60 דקות של מצב שיחה בחודש',
        'מורה ישראלי שנשמע טבעי',
        'תרגלו סלנג, דקדוק וכיוונים, כל מה שצריך',
        'קבעו קול זכר או נקבה פעם אחת ושכחו מזה',
        'סיטואציות יומיות בוואטסאפ',
      ],
    },
    proPlusComparison: {
      label:      'Pro מול Pro+',
      proHeader:  'Pro',
      plusHeader: 'Pro+',
      rows: [
        { label: 'מורה קולי בעברית', pro: 'לא כלול',           proPlus: '60 דקות בחודש' },
        { label: 'מצב שיחה',          pro: '15 דקות בחודש',     proPlus: '60 דקות בחודש' },
        { label: 'תגובות אבי המורה', pro: '300 בחודש',          proPlus: '1,000 בחודש' },
        { label: 'סיטואציות יומיות', pro: 'לא כלול',            proPlus: 'כלול' },
      ],
    },
    picker: {
      label:       'שפה',
      change:      'שינוי שפה',
      currentlyEn: 'אנגלית',
    },
  },

  limits: {
    monthlyReached:    'נגמרו לכם התווים החודש. שדרגו ל-Pro ל-100 פעמים יותר.',
    monthlyReachedPro: 'נגמרו לכם התווים לתקופה זו.',
    tooLong:           'הקטע ארוך מדי למסלול החינמי.',
    voiceMonthly:      'הגעתם למגבלה החודשית של השמעה קולית',
  },

  magicLink: {
    sending:     'שולח…',
    sent:        'בדקו את תיבת הדואר',
    sentDesc:    'לחצו על הקישור באימייל כדי להתחבר. הקישור תקף ל-10 דקות.',
    invalid:     'אנא הזינו כתובת אימייל תקינה',
    failed:      'שליחת קישור הקסם נכשלה',
    placeholder: 'name@example.com',
  },

  common: {
    close:  'סגור',
    cancel: 'ביטול',
    back:   'חזרה',
    retry:  'נסו שוב',
  },
}

export default strings
